import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import ClaimVXContract from '../../helpers/ClaimVXContract';
import VXContract from '../../helpers/VXContract';
import BearsContract from '../../helpers/BearsContract';
import HoneyDContract from '../../helpers/HONEYDContract';
import HoneyCostPopup from './HoneyCostPopup';
import fetchData from '../../helpers/fetchMetadata';
import config from '../../config';

interface VXClaimProps {
  signer?: ethers.Signer;
  provider?: ethers.providers.Provider;
  address?: string;
}

interface Bear {
  id: number;
  image: string;
  vxClaimed: boolean;
}

type ActiveView = 'claim' | 'check' | '';

const ETH_PRICE = 0.069;
const HONEY_PRICE = 100;

// Pre-defined list of forfeited VX IDs (from original project)
const FORFEITED_IDS = [
  3275, 3336, 5334, 5319, 5858, 3976, 2909, 5952, 3761, 4480,
  6054, 3773, 2715, 5699, 6296, 4711, 2437, 1510, 3934, 5722,
  2910, 6465, 1257, 572, 5724, 3932, 6038, 3777, 6369, 648,
  3597, 6102, 3252, 5213, 5171, 3598, 4011, 5513, 6485, 2402,
  3412, 3482, 6478, 602, 3487, 3660, 5028, 4463, 3317, 6049,
  1467, 3731, 3514, 5175, 2195, 1560, 3229, 6136, 1654, 3874,
  6305, 3544, 3736, 5528, 6520, 1485, 5712, 6363, 6246, 5471,
  6170, 4754, 5464, 3624, 4772, 2856, 5788, 4330, 5751, 3791,
  3384, 5374, 3592, 5645, 3451, 3937, 5444, 2886, 2969, 349,
  1630, 1567, 1495, 6365, 3818, 5934, 205, 3872, 5098, 5877,
  6031, 3385, 3664, 5502, 5860, 6058, 1575, 3528, 1394, 5886,
  5173, 289, 980, 5258, 5363, 5355, 5752, 516, 3689, 5803,
  3857, 5226, 5102, 1842, 2001, 2276, 1382, 5435, 669, 5114,
  1491, 2756, 6103, 3910, 5402, 1652, 6335, 1536, 4848, 5967,
  6197, 5825, 2790, 3337, 2853, 2631, 1235, 1505, 3708, 3195,
  3821, 4912, 5893, 5250, 3788, 1450, 6249, 2664, 4542, 3026,
  6152, 3760, 4467, 5457, 6236, 6016, 6461, 5765, 6023, 638,
  6091, 3406, 5477, 5159, 6128, 5717, 2069, 1678, 6195, 3751,
  5552, 1414, 5483, 3463, 1686, 5962, 3842, 330, 6325, 3849,
  3802, 1086, 6021, 6411, 1269, 1605, 3445, 1565, 6020, 6544,
  5256, 5352, 3455, 3655, 5404, 5954, 3992, 2955, 5619, 6144,
  354, 3968, 5498, 2940, 5874, 5195, 3179, 5953, 288, 3901,
  1246, 3720, 4533, 5549, 6089, 6385
];

const VXClaim: React.FC<VXClaimProps> = ({ signer, provider, address }) => {
  const [activeView, setActiveView] = useState<ActiveView>('');
  const [bears, setBears] = useState<Bear[]>([]);
  const [selectedBears, setSelectedBears] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [vxCheckId, setVxCheckId] = useState('');
  const [vxCheckResult, setVxCheckResult] = useState<boolean | null>(null);
  const [claimingStatus, setClaimingStatus] = useState(false);
  const [honeyBalance, setHoneyBalance] = useState<number>(0);

  // Read provider - bypasses MetaMask for read operations
  const readProvider = useMemo(() => {
    return new ethers.providers.JsonRpcProvider(config.infuraUrl);
  }, []);

  // Write contracts (need signer for transactions)
  const claimVXContract = useMemo(() => {
    return signer ? new ClaimVXContract(signer) : null;
  }, [signer]);

  const honeyContract = useMemo(() => {
    return signer ? new HoneyDContract(signer) : null;
  }, [signer]);

  // Read contracts (use direct RPC to bypass MetaMask rate limits)
  const vxContract = useMemo(() => {
    return new VXContract(readProvider);
  }, [readProvider]);

  const bearsContract = useMemo(() => {
    return new BearsContract(readProvider);
  }, [readProvider]);

  const claimVXReadContract = useMemo(() => {
    return new ClaimVXContract(readProvider);
  }, [readProvider]);

  const honeyReadContract = useMemo(() => {
    return new HoneyDContract(readProvider);
  }, [readProvider]);

  // Load user's honey balance
  const loadHoneyBalance = useCallback(async () => {
    if (!honeyReadContract || !address) return;
    
    try {
      const balance = await honeyReadContract.balanceOf(address);
      // Convert from wei to ether (HONEYD has 18 decimals)
      const formattedBalance = parseFloat(ethers.utils.formatEther(balance));
      setHoneyBalance(formattedBalance);
    } catch (error) {
      console.error('Error loading honey balance:', error);
      setHoneyBalance(0);
    }
  }, [honeyReadContract, address]);

  // Load user's bears and check VX claim status
  const loadBears = useCallback(async () => {
    if (!bearsContract || !vxContract || !address) return;
    
    setLoading(true);
    try {
      const tokenIds = await bearsContract.tokensOfOwner(address);
      console.log('Bear token IDs for address:', address, tokenIds.map((id: ethers.BigNumber) => id.toNumber()));
      
      const bearData = await Promise.all(
        tokenIds.map((id: ethers.BigNumber) => id.toNumber()).map(async (bearId: number) => {
          let vxClaimed = false;
          
          try {
            // Check if VX with this ID has already been minted
            // This is the key check - if vxExists returns true, 
            // the VX for this bear has already been claimed
            vxClaimed = await vxContract.exists(bearId);
            console.log(`Bear ${bearId} VX claimed status:`, vxClaimed);
          } catch (err) {
            console.error(`Error checking VX for bear ${bearId}:`, err);
            vxClaimed = false;
          }

          const metaRes = await fetchData(bearId, 'bears');
          const meta = await metaRes.json();

          return {
            id: bearId,
            image: meta.image,
            vxClaimed,
          };
        })
      );

      console.log('All bears loaded:', bearData);
      console.log('Bears with VX claimed:', bearData.filter(b => b.vxClaimed).map(b => b.id));
      console.log('Bears eligible for VX claim:', bearData.filter(b => !b.vxClaimed).map(b => b.id));
      
      setBears(bearData);
    } catch (error) {
      console.error('Error loading bears:', error);
    } finally {
      setLoading(false);
    }
  }, [bearsContract, vxContract, address]);

  // Load contract state
  const loadContractState = useCallback(async () => {
    if (!claimVXReadContract) return;

    try {
      const status = await claimVXReadContract.getClaimingStatus();
      setClaimingStatus(status);
    } catch (error) {
      console.error('Error loading contract state:', error);
    }
  }, [claimVXReadContract]);

  useEffect(() => {
    loadBears();
    loadContractState();
    loadHoneyBalance();
  }, [loadBears, loadContractState, loadHoneyBalance]);

  const toggleBear = (bearId: number) => {
    setSelectedBears(prev => 
      prev.includes(bearId) 
        ? prev.filter(id => id !== bearId)
        : [...prev, bearId]
    );
  };

  const claimVX = async (payHoney: boolean) => {
    if (!claimVXContract || selectedBears.length === 0) return;

    setProcessing(true);
    try {
      const ethValue = payHoney 
        ? ethers.BigNumber.from(0) 
        : ethers.utils.parseEther((ETH_PRICE * selectedBears.length).toFixed(4));

      const tx = await claimVXContract.claimBearsDeluxe(
        selectedBears,
        false, // forfeit
        payHoney,
        ethValue
      );
      await tx.wait();
      
      // Clear selection immediately
      setSelectedBears([]);
    } catch (error) {
      console.error('Error claiming VX:', error);
    }
    // Always reset processing state
    setProcessing(false);
    // Refresh data in background after modal closes
    loadBears().catch(console.error);
    loadContractState().catch(console.error);
    loadHoneyBalance().catch(console.error);
  };

  const checkVxExists = async () => {
    if (!vxContract || !vxCheckId) return;

    try {
      const id = parseInt(vxCheckId);
      
      // First check if it's in the forfeited list
      if (FORFEITED_IDS.includes(id)) {
        setVxCheckResult(true);
        return;
      }

      const exists = await vxContract.exists(id);
      setVxCheckResult(exists);
    } catch (error) {
      console.error('Error checking VX:', error);
      setVxCheckResult(null);
    }
  };

  // Filter bears that haven't claimed VX yet
  const unclaimedBears = bears.filter(b => !b.vxClaimed);
  const totalEthCost = (ETH_PRICE * selectedBears.length).toFixed(4);
  const totalHoneyCost = HONEY_PRICE * selectedBears.length;

  if (!address) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <div className="w-full p-6 bg-black bg-opacity-60 text-white shadow-md rounded-md border border-[#c4f6f9]">
          <h1 className="text-xl font-bold text-[#c4f6f9] mb-4">VX Deluxe</h1>
          <p className="text-sm text-[#c4f6f9]">Please connect your wallet to claim VX</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Header Section */}
      <div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-8 border border-[#c4f6f9]">
        <h1 className="text-xl font-bold text-[#c4f6f9] mb-2">VX Deluxe</h1>
        <div className="text-gray-300 text-sm">
          Bears Owned: <span className="text-white">{bears.length}</span>
          <span className="mx-3">|</span>
          Eligible for VX: <span className="text-[#ffa2f8]">{unclaimedBears.length}</span>
        </div>
      </div>

      {processing && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="bg-black bg-opacity-90 border-2 border-[#ffa2f8] p-8 rounded-lg text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffa2f8] mx-auto mb-4"></div>
            <div className="text-[#ffa2f8]">Processing transaction...</div>
          </div>
        </div>
      )}

      {/* Main Menu */}
      <div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-4">
        
        {/* Loading state while checking for eligible bears */}
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center text-sm text-[#c4f6f9]">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 mr-3 border-[#c4f6f9]"></div>
              Checking for eligible VX claims...
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap justify-center gap-4">
              {unclaimedBears.length > 0 && (
                <button
                  onClick={() => { setActiveView('claim'); setSelectedBears([]); }}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    activeView === 'claim' 
                      ? 'bg-transparent border-2 text-[#ffa2f8] border-[#ffa2f8]' 
                      : 'bg-transparent border-2 text-[#c4f6f9] border-[#c4f6f9] hover:bg-[#c4f6f9]/10'
                  }`}
                >
                  Claim VX ({unclaimedBears.length} eligible)
                </button>
              )}
              <button
                onClick={() => { setActiveView('check'); setSelectedBears([]); }}
                className={`px-4 py-2 rounded-md text-sm transition-colors ${
                  activeView === 'check' 
                    ? 'bg-transparent border-2 text-[#ffa2f8] border-[#ffa2f8]' 
                    : 'bg-transparent border-2 text-[#c4f6f9] border-[#c4f6f9] hover:bg-[#c4f6f9]/10'
                }`}
              >
                Check VX
              </button>
            </div>

            {/* No claimable VX message */}
            {unclaimedBears.length === 0 && bears.length > 0 && activeView === '' && (
              <div className="text-center text-gray-400 text-sm mt-6">
                All your bears have already claimed their VX!
              </div>
            )}

            {/* No bears owned message */}
            {bears.length === 0 && activeView === '' && (
              <div className="text-center text-gray-400 text-sm mt-6">
                You don't own any bears.
              </div>
            )}

            {activeView === '' && unclaimedBears.length > 0 && (
              <div className="text-center text-sm mt-6 text-[#c4f6f9]">
                Select an option above
              </div>
            )}
          </>
        )}

        {/* CLAIM VX Section */}
        {activeView === 'claim' && (
          <div className="mt-6">
            <p className="text-center text-sm mb-6 text-[#c4f6f9]">
              Select one or many bears to claim VX 1:1 Models for.
            </p>

            {!claimingStatus && (
              <div className="text-center text-red-400 text-sm mb-4">
                VX Claiming is currently closed
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <HoneyCostPopup
                addressHoney={honeyBalance}
                requiredHoney={totalHoneyCost}
                callback={() => claimVX(true)}
                callBackArgs={[]}
                trigger={
                  <button
                    disabled={selectedBears.length === 0 || processing || !claimingStatus}
                    className="px-4 py-2 bg-transparent rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#fcd34d]/10 border-2 border-[#fcd34d] text-[#fcd34d]"
                  >
                    Mint with HONEYD ({totalHoneyCost})
                  </button>
                }
              />
              <button
                onClick={() => claimVX(false)}
                disabled={selectedBears.length === 0 || processing || !claimingStatus}
                className="px-4 py-2 bg-transparent rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#55bac3]/10 border-2 border-[#55bac3] text-[#55bac3]"
              >
                Mint with ETH ({totalEthCost} ETH)
              </button>
            </div>

            <div className="text-center text-white text-sm mb-4">
              <span className="text-[#ffa2f8]">{selectedBears.length}</span> bear(s) selected
            </div>

            {loading ? (
              <div className="text-center text-gray-400">Loading bears...</div>
            ) : unclaimedBears.length === 0 ? (
              <div className="text-center text-gray-400">
                All your bears have already claimed their VX!
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {unclaimedBears.map(bear => (
                  <BearCard
                    key={bear.id}
                    bear={bear}
                    selected={selectedBears.includes(bear.id)}
                    onToggle={() => toggleBear(bear.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* CHECK VX Section */}
        {activeView === 'check' && (
          <div className="mt-6">
            <p className="text-center text-sm mb-6 text-[#c4f6f9]">
              Check if a VX has been minted for a specific bear ID
            </p>

            <div className="flex justify-center gap-4 mb-6">
              <input
                type="number"
                value={vxCheckId}
                onChange={(e) => {
                  setVxCheckId(e.target.value);
                  setVxCheckResult(null);
                }}
                placeholder="Enter Bear ID (1-6900)"
                min="1"
                max="6900"
                className="px-3 py-2 rounded-md bg-transparent border-2 border-white text-white placeholder-gray-400 focus:outline-none focus:border-[#ffa2f8] w-64 text-sm"
              />
              <button
                onClick={checkVxExists}
                disabled={!vxCheckId}
                className="px-4 py-2 bg-transparent rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#55bac3]/10 border-2 border-[#55bac3] text-[#55bac3]"
              >
                Check
              </button>
            </div>

            {vxCheckResult !== null && (
              <div className={`text-center text-sm ${vxCheckResult ? 'text-green-400' : 'text-red-400'}`}>
                VX #{vxCheckId} has {vxCheckResult ? 'been minted' : 'NOT been minted'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Owned VX Display */}
      {/* Future: Add gallery of owned VX here */}
    </div>
  );
};

// Bear Card Component
interface BearCardProps {
  bear: Bear;
  selected: boolean;
  onToggle: () => void;
  showVxStatus?: boolean;
}

const BearCard: React.FC<BearCardProps> = ({ bear, selected, onToggle, showVxStatus }) => {
  return (
    <div
      onClick={onToggle}
      className={`relative cursor-pointer rounded-md overflow-hidden transition-all bg-black bg-opacity-60 shadow-md ${
        selected ? 'border-2 border-[#ffa2f8]' : 'border-2 border-transparent hover:border-[#c4f6f9]'
      }`}
    >
      <img
        src={bear.image}
        alt={`Bear #${bear.id}`}
        className="w-full aspect-square object-cover"
      />
      <div className="p-2 bg-black bg-opacity-80">
        <div className="text-white text-sm text-center">#{bear.id}</div>
        {showVxStatus && (
          <div className={`text-xs text-center ${bear.vxClaimed ? 'text-green-400' : 'text-[#fcd34d]'}`}>
            {bear.vxClaimed ? 'VX Claimed' : 'VX Available'}
          </div>
        )}
      </div>
      {selected && (
        <div className="absolute top-2 right-2 rounded-full w-6 h-6 flex items-center justify-center text-black text-sm bg-[#ffa2f8]">
          ✓
        </div>
      )}
    </div>
  );
};

export default VXClaim;
